import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, MailQuestion, RotateCw, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const ITEMS: [string, string][] = [["maiden", "Maiden name & marriage dates"], ["addresses", "Address history"], ["dob", "Date of birth"], ["ssn", "SSN (by phone)"],
  ["license", "Driver's license"], ["signature", "Signature"], ["id", "Copy of ID"], ["other", "Other"]];
const LABEL = Object.fromEntries(ITEMS);
const when = (s: string) => new Date(s).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

const STATUS: Record<string, [string, string]> = {
  accepted: ["Accepted by email service", "bg-[#DCFCE7] text-[#166534]"],
  queued: ["Waiting to send (retrying)", "bg-[#FEF3C7] text-[#92400E]"],
  failed: ["Email failed, please resend", "bg-[#FEE2E2] text-[#991B1B]"],
};

export function InfoRequest({ c, locked, onDone }: { c: any; locked: boolean; onDone: () => void }) {
  const [items, setItems] = useState<string[]>([]);
  const [msg, setMsg] = useState("");
  const [hold, setHold] = useState(true);
  const [busy, setBusy] = useState(false);
  const email = c.data?.personal?.email;
  const reqs: any[] = [...(c.info_requests || [])].reverse();
  const toggle = (k: string) => setItems(items.includes(k) ? items.filter((x) => x !== k) : [...items, k]);
  const send = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/cases/${c.id}/info-request`, { items, message: msg, hold });
      if (r.email_status === "queued") toast.info(`Queued for ${email}, will send shortly`); else toast.success(`Request emailed to ${email}`);
      setItems([]); setMsg(""); onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const resolve = async (rid: string) => {
    try { await api.post(`/admin/cases/${c.id}/info-request/${rid}/resolve`); toast.success("Marked as received"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  const resend = async (rid: string) => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/cases/${c.id}/info-request/${rid}/resend`);
      if (r.email_status === "queued") toast.info(`Queued for ${r.to}, will send shortly`); else toast.success(`Request re-sent to ${r.to}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="card-soft overflow-hidden" data-testid="case-info-request">
      <div className="flex items-center gap-2 border-b border-[#E2E8F0] px-6 py-4">
        <MailQuestion className="h-5 w-5 text-[#F26B4F]" />
        <h3 className="font-bold text-[#1B365D]">More information needed</h3>
        {reqs.some((r) => !r.resolved_at) && <span className="ml-auto rounded-full bg-[#FEF3C7] px-2.5 py-0.5 text-xs font-semibold text-[#92400E]" data-testid="case-info-waiting">Waiting on applicant</span>}
      </div>
      <div className="space-y-4 px-6 py-5">
        {!email ? <p className="text-sm text-[#94A3B8]" data-testid="case-info-no-email">This applicant has no email address on file.</p> : !locked && (
          <>
            <div className="flex flex-wrap gap-2">{ITEMS.map(([k, l]) => (
              <button key={k} type="button" onClick={() => toggle(k)} data-testid={`case-info-item-${k}`}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${items.includes(k) ? "border-[#1B365D] bg-[#1B365D] text-white" : "border-[#CBD5E1] bg-white text-[#475569] hover:border-[#1B365D]"}`}>{l}</button>
            ))}</div>
            <textarea className="field-input min-h-[110px] py-3 text-sm" maxLength={3000} placeholder="Type exactly what you need, e.g. &quot;You answered that you married in June 2026, but didn't give your maiden name. Please reply with your last name at birth.&quot;"
              value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="case-info-message" />
            {items.includes("other") && !msg.trim() && <p className="text-xs font-semibold text-[#B45309]" data-testid="case-info-other-hint">Type what you need in the box above when choosing Other.</p>}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm text-[#475569]"><input type="checkbox" className="h-4 w-4 accent-[#1B365D]" checked={hold} onChange={(e) => setHold(e.target.checked)} data-testid="case-info-hold" /> Put case on hold</label>
              <button className="btn-primary btn-sm" disabled={busy || (!items.length && !msg.trim()) || (items.includes("other") && !msg.trim())} onClick={send} data-testid="case-info-send">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to applicant
              </button>
            </div>
            <p className="text-xs text-[#94A3B8]">Emails <b>{email}</b>{c.lang === "es" ? " in Spanish" : ""}. Their reply comes back to your reply-to inbox.</p>
          </>
        )}
        {reqs.length > 0 && (
          <div className="space-y-2 border-t border-[#E2E8F0] pt-4" data-testid="case-info-history">
            {reqs.map((r) => (
              <div key={r.id} className="rounded-lg border border-[#E2E8F0] bg-white p-3 text-sm" data-testid={`case-info-req-${r.id}`}>
                <div className="flex flex-wrap items-center gap-2 text-xs text-[#64748B]">
                  <span>{when(r.at)} · {r.by_name || r.by}</span>
                  {r.resolved_at ? <span className="ml-auto flex items-center gap-1 font-semibold text-[#15803D]" data-testid={`case-info-received-${r.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> {r.resolved_by === "applicant" ? "Applicant responded" : "Received"} {when(r.resolved_at)}</span>
                    : <span className="ml-auto flex items-center gap-3">
                        {!locked && <button className="flex items-center gap-1 font-semibold text-[#F26B4F] hover:underline disabled:opacity-50" disabled={busy} onClick={() => resend(r.id)} data-testid={`case-info-resend-${r.id}`}><RotateCw className="h-3.5 w-3.5" /> Resend</button>}
                        <button className="font-semibold text-[#1B365D] hover:underline" onClick={() => resolve(r.id)} data-testid={`case-info-resolve-${r.id}`}>Mark received</button>
                      </span>}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[#475569]" data-testid={`case-info-sent-to-${r.id}`}>
                  <span>Sent to <b className="font-mono">{r.to}</b>{r.resends?.length ? ` · re-sent ${r.resends.length}×, last ${when(r.resends[r.resends.length - 1].at)}` : ""}</span>
                  {STATUS[r.email_status] && <span className={`rounded-full px-2 py-0.5 font-semibold ${STATUS[r.email_status][1]}`} data-testid={`case-info-email-status-${r.id}`}>{STATUS[r.email_status][0]}</span>}
                </div>
                {r.items?.length > 0 && <p className="mt-1 font-semibold">{r.items.map((k: string) => LABEL[k] || k).join(" · ")}</p>}
                {r.message && <p className="mt-1 whitespace-pre-wrap text-[#475569]">{r.message}</p>}
                {(r.responses || []).map((x: any, j: number) => (
                  <div key={j} className="mt-2 rounded-md bg-[#F0FDF4] px-3 py-2 text-xs text-[#166534]" data-testid={`case-info-response-${r.id}-${j}`}>
                    <p className="font-semibold">Applicant replied {when(x.at)}{x.names_updated ? " · name history updated" : ""}{x.files?.length ? ` · ${x.files.length} file(s) added to Attachments` : ""}</p>
                    {x.reply && <p className="mt-1 whitespace-pre-wrap text-[#1A2536]">{x.reply}</p>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
